import React, { useState, useEffect, useCallback, useRef } from 'react';
import API, { fmt } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { downloadReport } from '@/lib/reportExports';
import { CashDateField } from '@/components/cash/CashDateField';
import { dateLabel } from '@/lib/cashReceipts';
import { toast } from 'sonner';
import { FileXls, FilePdf, ChartLineUp, CaretDown, CaretRight, FunnelSimple, Receipt, Wallet, Hourglass, ClipboardText, Percent } from '@phosphor-icons/react';

const reportAmount = value => value == null ? 'Onvolledig' : fmt(value);
const reportMargin = value => value == null ? '—' : `${value}%`;
const reportKey = item => item.rapport_id || item.factuur_id;

/** Verkooprapport — facturen, dagontvangsten en eventuele open bestelbonnen. */
export default function VerkoopRapportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isVerkoper = user?.role === 'verkoper';
  const hasAccess = isAdmin || (isVerkoper && !!user?.verkoper_id) || !!(user?.machtigingen || {}).verkooprapport_bekijken;

  const today = new Date();
  const eerste = new Date(today.getFullYear(), today.getMonth(), 1);
  // Lokale datum → 'YYYY-MM-DD' (toISOString zou in België één dag te vroeg geven)
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const MAANDEN = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

  const [van, setVan] = useState(ymd(eerste));
  const [tot, setTot] = useState(ymd(today));
  const [verkoperId, setVerkoperId] = useState('');
  const [klantId, setKlantId] = useState('');
  const [categorie, setCategorie] = useState('');
  const [inclBestelbonnen, setInclBestelbonnen] = useState(true); // ✅ default: bestelbonnen meetellen (verkoop onderweg)
  const [data, setData] = useState(null);
  const [verkopers, setVerkopers] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const loadVersion = useRef(0);
  const showProfit = !isVerkoper && (data?.mag_winst_zien ?? (isAdmin || !!user?.mag_winst_zien));

  useEffect(() => {
    // Niet-admins zien geen verkopers-dropdown — backend filtert toch alleen eigen data.
    if (isAdmin) {
      API.get('/verkopers').then(r => setVerkopers(r.data || [])).catch(() => {});
    }
    API.get('/klanten').then(r => setKlanten(r.data || [])).catch(() => {});
    API.get('/artikel-params').then(r => setCategorieen(r.data?.categorieen || [])).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    if (!hasAccess) { loadVersion.current += 1; setData(null); return; }
    const version = ++loadVersion.current;
    if (!van || !tot || van > tot) {
      setData({ items: [], fout: 'Kies een geldige begin- en einddatum.' }); setLoading(false); return;
    }
    setLoading(true);
    try {
      const params = { van, tot };
      if (verkoperId) params.verkoper_id = verkoperId;
      if (klantId) params.klant_id = klantId;
      if (categorie) params.categorie = categorie;
      params.inclusief_bestelbonnen = inclBestelbonnen;
      const r = await API.get('/verkoop-rapport', { params });
      if (version === loadVersion.current) setData(r.data);
    } catch (e) {
      if (version === loadVersion.current) setData({ items: [], aantal_facturen: 0, fout: e.response?.data?.detail || e.message });
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [van, tot, verkoperId, klantId, categorie, inclBestelbonnen, hasAccess, user?.id, user?._id]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (fid) => setExpanded(p => ({ ...p, [fid]: !p[fid] }));

  // Snelle periode-presets voor verkopers (en handig voor admins ook).
  // Stelt `van` en `tot` ineens in op de gekozen periode.
  const setPeriode = (preset) => {
    const nu = new Date();
    const y = nu.getFullYear();
    const m = nu.getMonth(); // 0-11
    let v, t;
    if (preset === 'deze_maand') {
      v = new Date(y, m, 1);
      t = new Date(y, m + 1, 0); // laatste dag huidige maand
    } else if (preset === 'vorige_maand') {
      v = new Date(y, m - 1, 1);
      t = new Date(y, m, 0); // laatste dag vorige maand
    } else if (preset === 'dit_jaar') {
      v = new Date(y, 0, 1);
      t = new Date(y, 11, 31);
    } else if (preset === 'vorig_jaar') {
      v = new Date(y - 1, 0, 1);
      t = new Date(y - 1, 11, 31);
    } else if (preset === 'dit_kwartaal') {
      const q = Math.floor(m / 3); // 0..3
      v = new Date(y, q * 3, 1);
      t = new Date(y, q * 3 + 3, 0);
    }
    if (v && t) { setVan(ymd(v)); setTot(ymd(t)); }
  };

  // Maand-/jaarkeuze: afgeleid uit van/tot (enkel "gevuld" als de periode exact één kalendermaand is)
  const maandSel = (() => {
    if (!van || !tot) return '';
    const dv = new Date(van + 'T00:00:00'), dt = new Date(tot + 'T00:00:00');
    const laatste = new Date(dv.getFullYear(), dv.getMonth() + 1, 0);
    return dv.getDate() === 1 && dt.getTime() === laatste.getTime() ? String(dv.getMonth() + 1) : '';
  })();
  const jaarSel = (van ? new Date(van + 'T00:00:00') : today).getFullYear();
  const kiesMaand = (m, j = jaarSel) => {
    if (!m) return;
    setVan(ymd(new Date(j, m - 1, 1)));
    setTot(ymd(new Date(j, m, 0)));
  };
  const kiesJaar = (j) => {
    if (maandSel) kiesMaand(parseInt(maandSel, 10), j);
    else { setVan(ymd(new Date(j, 0, 1))); setTot(ymd(new Date(j, 11, 31))); }
  };
  const JAREN = [...new Set([jaarSel, today.getFullYear() + 1, today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2, today.getFullYear() - 3])].sort((a, b) => b - a);

  // Label van de huidige geselecteerde periode (voor de widget-header).
  const periodeLabel = (() => {
    if (!van || !tot) return '';
    const dv = new Date(van), dt = new Date(tot);
    const fmt2 = (d) => d.toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${fmt2(dv)} → ${fmt2(dt)}`;
  })();

  const exportFile = async (format, detail) => {
    if (!van || !tot || van > tot) { toast.error('Kies een geldige begin- en einddatum.'); return; }
    const params = new URLSearchParams({ van, tot });
    if (verkoperId) params.set('verkoper_id', verkoperId);
    if (klantId) params.set('klant_id', klantId);
    if (categorie) params.set('categorie', categorie);
    params.set('inclusief_bestelbonnen', inclBestelbonnen);
    if (detail) params.set('detail', 'true');
    try { await downloadReport(`/verkoop-rapport/${format}`, params, `verkooprapport.${format === 'excel' ? 'xlsx' : 'pdf'}`); }
    catch { toast.error('Rapport kon niet worden gedownload. Probeer opnieuw.'); }
  };

  // Verkoper-vriendelijk export: alleen kolommen die de verkoper mag zien
  // (geen kost/winst/marge). Eén tabblad per verkoper + 'Totaal'-tabblad.
  const exportVerkoper = async (format) => {
    if (!van || !tot || van > tot) { toast.error('Kies een geldige begin- en einddatum.'); return; }
    const params = new URLSearchParams({ van, tot });
    if (verkoperId) params.set('verkoper_id', verkoperId);
    if (klantId) params.set('klant_id', klantId);
    if (categorie) params.set('categorie', categorie);
    params.set('inclusief_bestelbonnen', inclBestelbonnen);
    const path = format === 'excel' ? 'excel-verkoper' : 'pdf-verkoper';
    try { await downloadReport(`/verkoop-rapport/${path}`, params, `verkooprapport-verkopers.${format === 'excel' ? 'xlsx' : 'pdf'}`); }
    catch { toast.error('Rapport kon niet worden gedownload. Probeer opnieuw.'); }
  };

  if (!hasAccess) return <div role="alert" className="p-6" data-testid="sales-report-no-access">{isVerkoper ? 'Je verkoperkoppeling ontbreekt. Neem contact op met je beheerder.' : 'Je hebt geen toegang tot dit rapport.'}</div>;

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <ChartLineUp size={22} className="text-[#002FA7]" weight="duotone" />
        <h1 className="text-xl font-bold text-gray-900">
          {isVerkoper ? 'Mijn verkoop & commissie' : 'Verkooprapport'}
        </h1>
        <span className="text-xs text-gray-500">
          {isVerkoper ? 'Jouw eigen verkopen en commissie deze periode' : 'Facturen en dagontvangsten'}
        </span>
      </div>

      {/* "Mijn commissie" widget — enkel voor verkopers, prominent bovenaan (niet bij fout) */}
      {isVerkoper && data && !loading && !data.fout && (
        <MijnCommissieWidget
          data={data}
          periodeLabel={periodeLabel}
          verkoperNaam={user?.verkoper_naam || user?.name || 'Mijn commissie'}
          onPeriode={setPeriode}
        />
      )}

      {/* Filter-balk */}
      <div className="bg-white border border-gray-200 p-3 mb-4" data-testid="verkoop-rapport-filters">
        <div className="flex items-center gap-2 mb-2">
          <FunnelSimple size={14} className="text-gray-500" />
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <div>
            <label className="text-[10px] text-gray-500 block font-bold">Maand</label>
            <select className="field-input text-xs font-semibold" value={maandSel} onChange={e => kiesMaand(parseInt(e.target.value, 10))} data-testid="filter-maand">
              <option value="">— Kies maand —</option>
              {MAANDEN.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block font-bold">Jaar</label>
            <select className="field-input text-xs font-semibold" value={jaarSel} onChange={e => kiesJaar(parseInt(e.target.value, 10))} data-testid="filter-jaar">
              {JAREN.map(j => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="filter-van" className="text-[10px] text-gray-500 block font-bold">Van</label>
            <CashDateField value={van} onChange={setVan} id="filter-van" compact />
          </div>
          <div>
            <label htmlFor="filter-tot" className="text-[10px] text-gray-500 block font-bold">Tot</label>
            <CashDateField value={tot} onChange={setTot} id="filter-tot" compact />
          </div>
          {/* Verkoper-dropdown alleen voor admin. Verkopers zien automatisch enkel eigen data. */}
          {isAdmin && (
            <div>
              <label className="text-[10px] text-gray-500 block font-bold">Verkoper</label>
              <select className="field-input text-xs" value={verkoperId} onChange={e => setVerkoperId(e.target.value)} data-testid="filter-verkoper">
                <option value="">Alle verkopers</option>
                {verkopers.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] text-gray-500 block font-bold">Klant</label>
            <select className="field-input text-xs" value={klantId} onChange={e => setKlantId(e.target.value)} data-testid="filter-klant">
              <option value="">Alle klanten</option>
              {klanten.map(k => <option key={k.id} value={k.id}>{k.bedrijf?.trim() || k.bedrijfsnaam?.trim() || [k.voorn, k.achtern].filter(Boolean).join(' ').trim() || k.naam?.trim() || `Klant ${k.id}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block font-bold">Categorie</label>
            <select className="field-input text-xs" value={categorie} onChange={e => setCategorie(e.target.value)} data-testid="filter-categorie">
              <option value="">Alle categorieën</option>
              {categorieen.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 bg-amber-50 border border-amber-200 hover:bg-amber-100" data-testid="toggle-bestelbonnen-wrap">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={inclBestelbonnen}
                onChange={e => setInclBestelbonnen(e.target.checked)}
                data-testid="toggle-bestelbonnen"
              />
              <span className="text-xs font-bold text-amber-900">📋 Bestelbonnen meetellen <span className="font-normal text-gray-600 ml-1">(verkoop onderweg / nog niet gefactureerd)</span></span>
            </label>
            {/* Admin-only exports: tonen kost/winst/marge — verbergen voor verkopers */}
            {showProfit && (
              <>
                <button onClick={() => exportFile('excel', false)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-green-600 text-green-700 hover:bg-green-50" data-testid="export-xls-simpel">
                  <FileXls size={14} weight="bold" /> Excel (simpel)
                </button>
                <button onClick={() => exportFile('excel', true)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-green-600 text-green-700 hover:bg-green-50" data-testid="export-xls-detail">
                  <FileXls size={14} weight="bold" /> Excel + Detail
                </button>
                <button onClick={() => exportFile('pdf', false)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-red-600 text-red-700 hover:bg-red-50" data-testid="export-pdf-simpel">
                  <FilePdf size={14} weight="bold" /> PDF (simpel)
                </button>
                <button onClick={() => exportFile('pdf', true)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-red-600 text-red-700 hover:bg-red-50" data-testid="export-pdf-detail">
                  <FilePdf size={14} weight="bold" /> PDF + Detail
                </button>
                <span className="text-[10px] text-gray-400 ml-2 self-center">|</span>
              </>
            )}
            {/* Verkoper-vriendelijk: zonder kost/winst/marge — altijd zichtbaar (ook voor verkopers zelf) */}
            <button onClick={() => exportVerkoper('excel')} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-indigo-600 text-indigo-700 hover:bg-indigo-50" data-testid="export-xls-verkoper" title={isVerkoper ? 'Mijn verkopen in Excel (zonder kost/marge)' : 'Excel zonder kost/winst — 1 tabblad per verkoper + totaal-tabblad'}>
              <FileXls size={14} weight="bold" /> {isVerkoper ? 'Mijn Excel' : 'Excel per verkoper'}
            </button>
            <button onClick={() => exportVerkoper('pdf')} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-indigo-600 text-indigo-700 hover:bg-indigo-50" data-testid="export-pdf-verkoper" title={isVerkoper ? 'Mijn verkopen in PDF (zonder kost/marge)' : 'PDF zonder kost/winst — 1 sectie per verkoper'}>
              <FilePdf size={14} weight="bold" /> {isVerkoper ? 'Mijn PDF' : 'PDF per verkoper'}
            </button>
          </div>
          <button onClick={() => { setVerkoperId(''); setKlantId(''); setCategorie(''); setInclBestelbonnen(true); }} className="text-xs text-gray-500 hover:text-red-600 font-medium" data-testid="reset-filters">
            Filters wissen
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-gray-500">Laden...</div>}

      {/* Foutmelding-banner — vermijdt silent-fail wanneer backend 403/500 retourneert */}
      {data?.fout && !loading && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md" data-testid="verkooprapport-fout-banner">
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-lg">⚠️</span>
            <div className="flex-1">
              <div className="font-bold text-red-900 text-sm">Rapport kon niet geladen worden</div>
              <div className="text-red-800 text-xs mt-1">{data.fout}</div>
              {isVerkoper && (
                <div className="text-red-700 text-xs mt-2 italic">
                  Probeer de pagina te herladen. Blijft het probleem? Vraag een beheerder om uw <strong>verkoper-koppeling</strong> te verifiëren.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {data && !loading && !data.fout && (
        <>
          {data.bestelbonnen_gefactureerd_overgeslagen > 0 && (
            <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 flex items-center gap-2" data-testid="bb-gefactureerd-info">
              <Receipt size={14} weight="bold" className="text-emerald-700 shrink-0" />
              <span><strong>{data.bestelbonnen_gefactureerd_overgeslagen} bestelbon{data.bestelbonnen_gefactureerd_overgeslagen > 1 ? 'nen' : ''}</strong> uit deze periode {data.bestelbonnen_gefactureerd_overgeslagen > 1 ? 'zijn' : 'is'} al gefactureerd en {data.bestelbonnen_gefactureerd_overgeslagen > 1 ? 'worden' : 'wordt'} niet als "verkoop onderweg" geteld — de factuur telt (op factuurdatum).</span>
            </div>
          )}
          {data.aantal_dagontvangsten > 0 && <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 text-xs text-emerald-950 flex items-center gap-2 flex-wrap" data-testid="sales-cash-summary">
            <Wallet size={16} /><strong data-testid="sales-cash-count">{data.aantal_dagontvangsten} dagontvangsten inbegrepen</strong>
            <span data-testid="sales-cash-revenue">Omzet {fmt(data.dagontvangsten_omzet)} excl. btw</span>
            {showProfit && <span data-testid="sales-cash-profit">· Winst {reportAmount(data.dagontvangsten_winst)}</span>}
          </div>}
          {showProfit && data.winst_onvolledig && <div className="mb-3 px-3 py-2 bg-amber-50 text-amber-900 text-xs border border-amber-200" role="status" data-testid="sales-profit-incomplete">Winst onvolledig: bij een dagontvangst ontbreekt de inkoopprijs.</div>}
          {/* KPI tegels — alleen voor admins (verkopers zien de info al in de Mijn commissie widget bovenaan) */}
          {!isVerkoper && (
            <>
              {/* Bedrag-KPIs: Omzet · Ontvangen · Te ontvangen */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="kpi-card border-l-4 border-l-[#002FA7]" data-testid="kpi-verkoop-omzet">
                  <div className="kpi-label">💰 Totaal omzet</div>
                  <div className="kpi-value text-[#002FA7]">{fmt(data.totaal_omzet)}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{data.aantal_documenten ?? data.aantal_facturen} verkopen</div>
                </div>
                <div className="kpi-card border-l-4 border-l-[#008A27]" data-testid="kpi-verkoop-betaald">
                  <div className="kpi-label">✓ Ontvangen</div>
                  <div className="kpi-value text-[#008A27]">{fmt(data.totaal_betaald || 0)}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">Facturen en cashverkopen, excl. btw</div>
                </div>
                <div className="kpi-card border-l-4 border-l-[#E5B300]" data-testid="kpi-verkoop-te-ontvangen">
                  <div className="kpi-label">⏳ Te ontvangen</div>
                  <div className="kpi-value text-[#E5B300]">{fmt(data.totaal_te_ontvangen || 0)}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">Openstaand saldo</div>
                </div>
              </div>
              {/* Marge/commissie KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <KPI label="Verkopen" waarde={data.aantal_documenten ?? data.aantal_facturen} kleur="#002FA7" />
                <KPI label="Omzet" waarde={fmt(data.totaal_omzet)} kleur="#002FA7" />
                {showProfit && <KPI label="Winst bruto" waarde={reportAmount(data.totaal_winst_bruto)} sub={reportMargin(data.marge_bruto_pct)} kleur="#008A27" />}
                <KPI label="Commissies" waarde={fmt(data.totaal_commissie)} sub={data.totaal_commissie_pending > 0 ? `+ ${fmt(data.totaal_commissie_pending)} pending` : ''} kleur="#F97316" />
                {showProfit && <KPI label="Winst netto" waarde={reportAmount(data.totaal_winst_netto)} sub={reportMargin(data.marge_netto_pct)} kleur="#008A27" highlight />}
              </div>
            </>
          )}

          {data.totaal_commissie_pending > 0 && (
            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-2" data-testid="pending-commissie-info">
              <span className="font-bold">⏳ Pending commissie:</span>
              <span>{fmt(data.totaal_commissie_pending)} aan commissie zal pas worden toegekend zodra de bestelbon(nen) worden omgezet in een factuur.</span>
            </div>
          )}

          {/* Facturen-tabel */}
          {data.items && data.items.length > 0 ? (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                <thead className="bg-[#F9FAFB] text-[10px] uppercase font-bold text-gray-600">
                  <tr>
                    <th className="w-6 px-2 py-2"></th>
                    <th className="px-2 py-2 text-left" data-testid="sales-date-header">Datum</th>
                    <th className="px-2 py-2 text-left">Klant</th>
                    {!isVerkoper && <th className="px-2 py-2 text-left">Verkoper</th>}
                    <th className="px-2 py-2 text-right">Omzet</th>
                    {showProfit && <th className="px-2 py-2 text-right">Kost</th>}
                    {showProfit && <th className="px-2 py-2 text-right">Winst bruto</th>}
                    {showProfit && <th className="px-2 py-2 text-right">Marge %</th>}
                    <th className="px-2 py-2 text-right">{isVerkoper ? 'Mijn commissie' : 'Commissie'}</th>
                    {showProfit && <th className="px-2 py-2 text-right">Winst netto</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...(data.items || [])].sort((a, b) => {
                    // Nieuwste altijd bovenaan: datum desc, dan factuur-ID desc (bv. FAC-2026-00020 > FAC-2026-00019)
                    const da = a.datum || ''; const db = b.datum || '';
                    if (da !== db) return db.localeCompare(da);
                    return (b.factuur_id || '').localeCompare(a.factuur_id || '');
                  }).map(it => (
                    <React.Fragment key={reportKey(it)}>
                      <tr
                        className="cursor-pointer hover:bg-[#F0F4FF] border-t border-gray-100"
                        tabIndex={0}
                        aria-expanded={!!expanded[reportKey(it)]}
                        onClick={() => toggleExpand(reportKey(it))}
                        onKeyDown={e => { if (e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); toggleExpand(reportKey(it)); } }}
                        data-testid={`rapport-row-${reportKey(it)}`}
                      >
                        <td className="px-2 py-1.5">{expanded[reportKey(it)] ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
                        <td className="px-2 py-1.5 font-mono" data-testid={`sales-date-${reportKey(it)}`}>
                          {dateLabel(it.datum)}
                          {it.doc_type === 'dagontvangst' && <span className="ml-1 text-[8px] px-1 py-0.5 bg-emerald-100 text-emerald-800 font-bold" data-testid={`cash-badge-${it.dagontvangst_id}`}>CASH</span>}
                          {it.doc_type === 'bestelbon' && (
                            <span className="ml-1 text-[8px] font-bold px-1 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" title="Nog niet gefactureerd — bestelbon uit verkoop onderweg" data-testid={`bestelbon-badge-${it.factuur_id}`}>
                              BESTELBON
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-[200px]">{it.klant_naam}</td>
                        {!isVerkoper && <td className="px-2 py-1.5 text-gray-700">{it.verkoper_naam}</td>}
                        <td className="px-2 py-1.5 text-right font-mono" data-testid={`sales-revenue-${reportKey(it)}`}>{fmt(it.omzet)}</td>
                        {showProfit && <td className="px-2 py-1.5 text-right font-mono text-gray-500" data-testid={`sales-cost-${reportKey(it)}`}>{reportAmount(it.kost)}</td>}
                        {showProfit && <td className="px-2 py-1.5 text-right font-mono font-bold text-[#008A27]" data-testid={`sales-gross-profit-${reportKey(it)}`}>{reportAmount(it.winst_bruto)}</td>}
                        {showProfit && <td className="px-2 py-1.5 text-right text-xs text-gray-600">{reportMargin(it.marge_bruto_pct)}</td>}
                        <td className="px-2 py-1.5 text-right font-mono text-[#F97316]">
                          {it.doc_type === 'bestelbon' && it.commissie_pending > 0 ? (
                            <span className="italic text-amber-700" title="Pending — wordt commissie zodra bestelbon → factuur">⏳ {fmt(it.commissie_pending)}</span>
                          ) : fmt(it.commissie)}
                        </td>
                        {showProfit && (
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-[#008A27] bg-green-50/30" data-testid={`sales-net-profit-${reportKey(it)}`}>
                            {reportAmount(it.winst_netto)}
                            {it.doc_type === 'bestelbon' && <span className="block text-[9px] text-amber-700 italic font-normal">(zonder commissie)</span>}
                          </td>
                        )}
                      </tr>
                      {expanded[reportKey(it)] && (
                        <tr>
                          <td colSpan={isVerkoper ? 5 : showProfit ? 10 : 6} className="p-0 bg-[#F9FAFB]">
                            <div className="p-3 border-t border-gray-200">
                              <div className="text-[11px] font-bold text-gray-700 mb-2">Artikelregels ({it.aantal_regels}):</div>
                              <table className="w-full text-[11px]">
                                <thead className="text-[9px] uppercase text-gray-500">
                                  <tr>
                                    <th className="text-left py-1">Artikel</th>
                                    <th className="text-left">Omschrijving</th>
                                    <th className="text-left">Categorie</th>
                                    <th className="text-right">Aant.</th>
                                    <th className="text-right">Prijs</th>
                                    {showProfit && <th className="text-right">Kost</th>}
                                    <th className="text-right">Omzet</th>
                                    {showProfit && <th className="text-right">Winst</th>}
                                    {showProfit && <th className="text-right">Marge %</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {it.artikelregels.map((r, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                      <td className="py-1 font-mono font-bold">{r.art_code}</td>
                                      <td className="text-gray-700">{r.omschrijving}</td>
                                      <td>{r.categorie && <span className="text-[9px] px-1.5 py-0.5 bg-purple-50 text-purple-600 font-bold rounded-full">{r.categorie}</span>}</td>
                                      <td className="text-right">{r.aantal}</td>
                                      <td className="text-right font-mono">{fmt(r.netto_prijs)}</td>
                                      {showProfit && <td className="text-right font-mono text-gray-500">{reportAmount(r.aankoop)}</td>}
                                      <td className="text-right font-mono">{fmt(r.omzet)}</td>
                                      {showProfit && <td className="text-right font-mono font-bold text-[#008A27]">{reportAmount(r.winst)}</td>}
                                      {showProfit && <td className="text-right text-gray-600">{reportMargin(r.marge_pct)}</td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {it.commissie_details && it.commissie_details.length > 0 && (
                                <>
                                  <div className="text-[11px] font-bold text-[#F97316] mt-3 mb-1">Commissie-breakdown:</div>
                                  <div className="flex flex-wrap gap-2 text-[10px]">
                                    {it.commissie_details.map((d, i) => (
                                      <span key={i} className="px-2 py-0.5 bg-orange-50 text-orange-800 border border-orange-200 rounded">
                                        {d.art_code} <span className="text-gray-500">({d.scope}: {d.doel})</span> → <strong>€{d.bedrag.toFixed(2)}</strong>
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white border border-gray-200">
              Geen verkopen gevonden met deze filters.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, waarde, sub, kleur, highlight }) {
  return (
    <div className={`bg-white border ${highlight ? 'border-[#008A27] border-2' : 'border-gray-200'} p-3`} data-testid={`sales-kpi-${label.toLowerCase().replaceAll(' ', '-')}`}>
      <div className="text-[10px] uppercase font-bold tracking-wide text-gray-500">{label}</div>
      <div className="text-lg font-bold mt-1" style={{ color: kleur }}>{waarde}</div>
      {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
    </div>
  );
}

/**
 * MijnCommissieWidget — Prominent dashboard bovenaan voor verkopers.
 * Toont eigen commissie deze periode + quick-period buttons. Hergebruikt
 * `data` van het verkoop-rapport endpoint (al verkoper-gefilterd door backend).
 */
function MijnCommissieWidget({ data, periodeLabel, verkoperNaam, onPeriode }) {
  const totaalCommissie = Number(data?.totaal_commissie || 0);
  const pendingCommissie = Number(data?.totaal_commissie_pending || 0);
  const aantalBons = Number(data?.aantal_documenten ?? data?.aantal_facturen ?? 0);
  const omzet = Number(data?.totaal_omzet || 0);
  const effectiefPct = omzet > 0 ? (totaalCommissie / omzet) * 100 : 0;

  return (
    <div
      className="relative overflow-hidden mb-5 rounded-xl shadow-lg border border-orange-300/40"
      style={{ background: 'linear-gradient(135deg, #FF6B0D 0%, #F97316 45%, #FB923C 100%)' }}
      data-testid="mijn-commissie-widget"
    >
      {/* Subtiel patroon op de achtergrond */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.3) 0%, transparent 35%)',
        }}
      />

      <div className="relative px-5 py-4 text-white">
        {/* Header met naam + periode */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Mijn commissie</div>
            <div className="text-xl font-extrabold mt-0.5" data-testid="widget-verkoper-naam">{verkoperNaam}</div>
            <div className="text-[11px] opacity-80 mt-0.5">{periodeLabel}</div>
          </div>
          {/* Quick-period buttons */}
          <div className="inline-flex items-center gap-1 flex-wrap" data-testid="quick-periode-buttons">
            <button
              onClick={() => onPeriode('deze_maand')}
              className="bg-white/15 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              data-testid="periode-deze-maand"
            >Deze maand</button>
            <button
              onClick={() => onPeriode('vorige_maand')}
              className="bg-white/15 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              data-testid="periode-vorige-maand"
            >Vorige maand</button>
            <button
              onClick={() => onPeriode('dit_kwartaal')}
              className="bg-white/15 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              data-testid="periode-dit-kwartaal"
            >Dit kwartaal</button>
            <button
              onClick={() => onPeriode('dit_jaar')}
              className="bg-white/15 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              data-testid="periode-dit-jaar"
            >Dit jaar</button>
            <button
              onClick={() => onPeriode('vorig_jaar')}
              className="bg-white/15 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              data-testid="periode-vorig-jaar"
            >Vorig jaar</button>
          </div>
        </div>

        {/* Hoofd-KPI's */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Verdiende commissie */}
          <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2.5 border border-white/20" data-testid="kpi-commissie-totaal">
            <div className="flex items-center gap-1.5 mb-1">
              <Wallet size={14} weight="fill" />
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-90">Verdiend</span>
            </div>
            <div className="text-2xl font-extrabold tabular-nums font-mono">
              {fmt(totaalCommissie)}
            </div>
            <div className="text-[10px] opacity-75 mt-0.5">Bevestigd (facturen)</div>
          </div>

          {/* Pending commissie */}
          <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2.5 border border-white/20" data-testid="kpi-commissie-pending">
            <div className="flex items-center gap-1.5 mb-1">
              <Hourglass size={14} weight="fill" />
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-90">Pending</span>
            </div>
            <div className="text-2xl font-extrabold tabular-nums font-mono">
              {fmt(pendingCommissie)}
            </div>
            <div className="text-[10px] opacity-75 mt-0.5">
              {pendingCommissie > 0 ? 'Wacht op facturatie' : 'Geen open bestelbonnen'}
            </div>
          </div>

          {/* Aantal verkopen */}
          <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2.5 border border-white/20" data-testid="kpi-aantal-bons">
            <div className="flex items-center gap-1.5 mb-1">
              <ClipboardText size={14} weight="fill" />
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-90">Verkopen</span>
            </div>
            <div className="text-2xl font-extrabold tabular-nums font-mono">{aantalBons}</div>
            <div className="text-[10px] opacity-75 mt-0.5">Facturen, dagontvangsten en bestelbonnen</div>
          </div>

          {/* Effectief % */}
          <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2.5 border border-white/20" data-testid="kpi-effectief-pct">
            <div className="flex items-center gap-1.5 mb-1">
              <Percent size={14} weight="fill" />
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-90">Effectief</span>
            </div>
            <div className="text-2xl font-extrabold tabular-nums font-mono">
              {effectiefPct.toFixed(2).replace('.', ',')}%
            </div>
            <div className="text-[10px] opacity-75 mt-0.5">Op {fmt(omzet)} omzet</div>
          </div>
        </div>

        {/* Totaal te verwachten (bevestigd + pending) */}
        {pendingCommissie > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 bg-white text-orange-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-testid="totaal-te-verwachten">
            <Wallet size={13} weight="fill" />
            Totaal te verwachten: <span className="text-orange-800 text-base font-mono">{fmt(totaalCommissie + pendingCommissie)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
